import FAB from '../UI/FAB';
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
    Card, 
    Tabs, 
    Typography, 
    Space, 
    Button, 
    Row, 
    Col,
    Statistic,
    Tag,
    Divider,
    Input
} from 'antd';
import { 
    SearchOutlined, 
    ShopOutlined, 
    MenuOutlined, 
    FireOutlined,
    HistoryOutlined,
    BarChartOutlined
} from '@ant-design/icons';
import AdvancedSearch from '../Search/AdvancedSearch';
import SearchResults from '../Search/SearchResults';
import { useSearch } from '../../contexts/SearchContext';
import FoodCard from '../UI/FoodCard';
import { useWishlist } from '../../contexts/WishlistContext';
import { useCart } from '../../contexts/CartContext';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

function Search() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { 
        search, 
        searchResults, 
        loading, 
        searchHistory, 
        trendingSearches,
        getSearchAnalytics,
        searchType,
        setSearchType,
        pagination,
        clearSearch
    } = useSearch();

    const [activeTab, setActiveTab] = useState('vendors');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchTimeout, setSearchTimeout] = useState(null);

    // Initialize search from URL params
    useEffect(() => {
        const query = searchParams.get('q') || '';
        const type = searchParams.get('type') || 'menu';
        
        setSearchQuery(query);
        setActiveTab(type);
        setSearchType(type);

        if (query) {
            handleSearch(query, type);
        }
    }, [searchParams]);

    // Re-run search when pagination changes
    useEffect(() => {
        if (!searchQuery) return;
        handleSearch(searchQuery, activeTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.current, pagination.pageSize]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    // Handle search
    const handleSearch = async (query, type = activeTab) => {
        try {
            await search(query, {}, type);
            
            // Update URL
            const newParams = new URLSearchParams();
            if (query) newParams.set('q', query);
            if (type !== 'menu') newParams.set('type', type);
            setSearchParams(newParams);
        } catch (error) {
            console.error('Search failed:', error);
        }
    };

    // Handle tab change
    const handleTabChange = (key) => {
        setActiveTab(key);
        setSearchType(key);
        
        if (searchQuery) {
            handleSearch(searchQuery, key);
        }
    };

    // Handle item click
    const handleItemClick = (item) => {
        if (activeTab === 'vendors') {
            navigate(`/vendors/${item.id}`);
        } else if (activeTab === 'menu') {
            navigate(`/menu?dish=${item.id}`);
        }
    };

    // Get search analytics
    const analytics = getSearchAnalytics();

    // Render search stats
    const renderSearchStats = () => (
        <Row gutter={[12, 12]} className="mb-6" data-emoji-context="analytics">
            <Col xs={12} md={6}>
                <Card size="small">
                    <Statistic
                        title="Total Searches"
                        value={analytics.totalSearches}
                        prefix={<SearchOutlined />}
                    />
                </Card>
            </Col>
            <Col xs={12} md={6}>
                <Card size="small">
                    <Statistic
                        title="Unique Queries"
                        value={analytics.uniqueQueries}
                        prefix={<BarChartOutlined />}
                    />
                </Card>
            </Col>
            <Col xs={12} md={6}>
                <Card size="small">
                    <Statistic
                        title="Today's Searches"
                        value={analytics.searchTrends.today}
                        prefix={<FireOutlined />}
                    />
                </Card>
            </Col>
            <Col xs={12} md={6}>
                <Card size="small">
                    <Statistic
                        title="This Week"
                        value={analytics.searchTrends.thisWeek}
                        prefix={<HistoryOutlined />}
                    />
                </Card>
            </Col>
        </Row>
    );

    // Render search suggestions
    const renderSearchSuggestions = () => {
        if (searchHistory.length === 0 && trendingSearches.length === 0) return null;

        return (
            <div className="mb-6">
                <Row gutter={[12, 12]}>
                    {/* Search History */}
                    {searchHistory.length > 0 && (
                        <Col xs={24} md={12}>
                            <Card title="Recent Searches" size="small">
                                <div className="space-y-2">
                                    {searchHistory.slice(0, 5).map((item, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer"
                                            onClick={() => handleSearch(item.query, item.type)}
                                        >
                                            <Text>{item.query}</Text>
                                            <Tag size="small">{item.type}</Tag>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        </Col>
                    )}

                    {/* Trending Searches */}
                    {trendingSearches.length > 0 && (
                        <Col xs={24} md={12}>
                            <Card title="Trending Searches" size="small">
                                <div className="space-y-2">
                                    {trendingSearches.slice(0, 5).map((item, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer"
                                            onClick={() => handleSearch(item.query)}
                                        >
                                            <Text>{item.query}</Text>
                                            <Tag size="small" color="orange">
                                                {item.count}
                                            </Tag>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        </Col>
                    )}
                </Row>
            </div>
        );
    };

    // Render most searched terms
    const renderMostSearched = () => {
        if (!analytics.mostSearched || analytics.mostSearched.length === 0) return null;

        return (
            <Card title="Most Searched Terms" className="mb-6" size="small">
                <div className="space-y-2">
                    {analytics.mostSearched.map((item, index) => (
                        <div
                            key={index}
                            className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer"
                            onClick={() => handleSearch(item.query)}
                        >
                            <div className="flex items-center gap-2">
                                <Text strong>#{index + 1}</Text>
                                <Text>{item.query}</Text>
                            </div>
                            <Tag color="blue">{item.count} searches</Tag>
                        </div>
                    ))}
                </div>
            </Card>
        );
    };

    const { toggleWishlist, isInWishlist } = useWishlist();
    const { addToCart } = useCart();
    const menuResults = searchResults.menu || [];

    const handleAddToCart = (item) => {
        addToCart({
            id: item.id,
            name: item.name,
            price: item.price || 0,
            image: item.image,
            quantity: 1
        });
    };

    const handleToggleWishlist = (item) => {
        toggleWishlist(item, 'menu');
    };

    return (
        <div className="min-h-screen bg-[#FAF8F5]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {/* Header Section */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl md:text-5xl font-bold text-[#2C2C2C] mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
                        Our Menu
                    </h1>
                    <p className="text-base md:text-lg text-[#4A4A4A] max-w-2xl mx-auto">
                        Discover delicious dishes from our verified catering partners
                    </p>
                </div>

                {/* Search Bar */}
                <div className="max-w-2xl mx-auto mb-12">
                    <Input
                        size="large"
                        placeholder="Search dishes..."
                        value={searchQuery}
                        onChange={(e) => {
                            const value = e.target.value;
                            setSearchQuery(value);
                            
                            // Clear existing timeout
                            if (searchTimeout) {
                                clearTimeout(searchTimeout);
                            }
                            
                            // Debounce search - wait 500ms after user stops typing
                            if (value.trim()) {
                                const timeout = setTimeout(() => {
                                    handleSearch(value.trim(), 'menu');
                                }, 500);
                                setSearchTimeout(timeout);
                            } else {
                                // Clear results if search is empty
                                clearSearch();
                            }
                        }}
                        onPressEnter={(e) => {
                            if (e.target.value.trim()) {
                                if (searchTimeout) {
                                    clearTimeout(searchTimeout);
                                }
                                handleSearch(e.target.value.trim(), 'menu');
                            }
                        }}
                        className="h-12 rounded-lg border-gray-300 focus:border-[#C97D60] focus:ring-[#C97D60]"
                        prefix={<SearchOutlined className="text-[#4A4A4A]" />}
                    />
                </div>

                {/* Search Results Grid */}
                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <Card key={i} loading className="h-96" />
                        ))}
                    </div>
                ) : menuResults.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {menuResults.map((menu) => (
                            <FoodCard
                                key={menu.id}
                                variant="menu"
                                item={menu}
                                imageHeight={200}
                                onView={() => navigate(`/menu/${menu.id}`)}
                                onAddToCart={handleAddToCart}
                                onFavorite={handleToggleWishlist}
                                isFavorite={isInWishlist(menu.id, 'menu')}
                                actionsPlacement="footer"
                                badges={[
                                    ...(menu.is_vegetarian ? [{ text: 'Veg', color: 'green' }] : []),
                                    ...(menu.is_spicy ? [{ text: 'Spicy', color: 'red' }] : [])
                                ]}
                            />
                        ))}
                    </div>
                ) : searchQuery ? (
                    <div className="text-center py-12">
                        <SearchOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />
                        <Title level={3} className="mt-4 text-[#4A4A4A]">
                            No results found
                        </Title>
                        <Paragraph className="text-[#4A4A4A]">
                            Try adjusting your search terms
                        </Paragraph>
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <SearchOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />
                        <Title level={3} className="mt-4 text-[#4A4A4A]">
                            Start Your Search
                        </Title>
                        <Paragraph className="text-[#4A4A4A]">
                            Enter your search terms above to find dishes
                        </Paragraph>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Search;
